/**
 * Session lifecycle: sign in, keep the access token fresh, sign out.
 *
 * Kept out of React on purpose. Refresh has to be single-flight — the refresh token *rotates*, so
 * two concurrent refreshes mean the second one presents a token the first already spent, and the
 * session dies. A module-level promise is the whole mechanism, and it is testable without a
 * renderer.
 */

import * as authApi from "../api/auth-api";
import { ApiError } from "../api/errors";
import type { Account, Session } from "../api/types";
import * as store from "./token-store";

function remember(session: Session): void {
  store.setSession({
    accessToken: session.access_token,
    refreshToken: session.refresh_token,
    expiresIn: session.expires_in,
  });
}

export async function signIn(email: string, password: string): Promise<Account> {
  const session = await authApi.login(email, password);
  remember(session);
  return authApi.me(session.access_token);
}

/**
 * Register, then sign in with the same credentials.
 *
 * Two calls because the API makes it two: `/auth/register` answers 201 with an account and no
 * session, so a dashboard that stopped there would leave a new user on a sign-in form typing the
 * password they just typed.
 */
export async function registerAndSignIn(email: string, password: string): Promise<Account> {
  await authApi.register(email, password);
  return signIn(email, password);
}

/** In flight, if a refresh is. Everything that needs a token awaits this rather than starting one. */
let inFlight: Promise<string> | null = null;

async function rotate(refreshToken: string): Promise<string> {
  const session = await authApi.refresh(refreshToken);
  remember(session);
  return session.access_token;
}

/**
 * An access token that is good for the next minute, refreshing if required.
 *
 * Throws `ApiError` when there is nothing to refresh from, or when the refresh token has expired
 * or been spent. Either way the local session is cleared first: holding a refresh token the server
 * has already rejected only produces the same failure again.
 */
export async function getFreshAccessToken(): Promise<string> {
  const current = store.getAccessToken();
  if (current !== null && store.isAccessTokenUsable()) return current;

  if (inFlight !== null) return inFlight;

  const refreshToken = store.getRefreshToken();
  if (refreshToken === null) {
    store.clearSession();
    throw new ApiError("Your session has ended. Sign in again.", {
      code: "unauthenticated",
      status: 401,
    });
  }

  inFlight = rotate(refreshToken)
    .catch((error: unknown) => {
      // A refused refresh is terminal — the token rotated or expired. A network failure is not,
      // but the distinction only matters for the message: either way there is no usable token now.
      if (error instanceof ApiError && error.code !== "network_error") store.clearSession();
      throw error;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/** Whether a session could plausibly be restored. Cheap, synchronous, no network. */
export function hasStoredSession(): boolean {
  return store.getAccessToken() !== null || store.getRefreshToken() !== null;
}

/** Restore on boot: exchange the stored refresh token for a live session. `null` if there is none. */
export async function restore(): Promise<Account | null> {
  if (!hasStoredSession()) return null;
  try {
    const accessToken = await getFreshAccessToken();
    return await authApi.me(accessToken);
  } catch (error) {
    if (error instanceof ApiError && error.isAuthFailure) {
      store.clearSession();
      return null;
    }
    throw error;
  }
}

/**
 * Revoke the session server-side, then forget it locally.
 *
 * The local half happens whatever the network did. A sign-out that leaves a token behind because
 * the request timed out is a sign-out that did not happen, and the person walking away from the
 * laptop has no way to know.
 */
export async function signOut(): Promise<void> {
  const refreshToken = store.getRefreshToken();
  try {
    if (refreshToken !== null) await authApi.logout(refreshToken);
  } catch {
    /* deliberately swallowed — see above */
  } finally {
    store.clearSession();
  }
}

/** Test seam. */
export function __resetForTests(): void {
  inFlight = null;
  store.__resetForTests();
}
